import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPatch, apiDelete, operationsClient } from '@/lib/api';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';

export interface ScopeOfWorkTemplate {
  id: string;
  tenantId: string;
  name: string;
  content: string;
  /** At most one per tenant; pickers list it first. */
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export function useScopeOfWorkTemplates() {
  return useQuery({
    queryKey: settingsKeys.scopeOfWorkTemplates(),
    queryFn: () =>
      apiGet<ScopeOfWorkTemplate[]>(operationsClient, settingsEndpoints.scopeOfWorkTemplates()),
  });
}

export function useCreateScopeOfWorkTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: { name: string; content: string; isDefault?: boolean }) =>
      apiPost<ScopeOfWorkTemplate>(operationsClient, settingsEndpoints.scopeOfWorkTemplates(), dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.scopeOfWorkTemplates() });
    },
  });
}

export function useUpdateScopeOfWorkTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...dto
    }: {
      id: string;
      name?: string;
      content?: string;
      isDefault?: boolean;
    }) =>
      apiPatch<ScopeOfWorkTemplate>(
        operationsClient,
        settingsEndpoints.scopeOfWorkTemplateDetail(id),
        dto,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.scopeOfWorkTemplates() });
    },
  });
}

/**
 * Promote a template to the tenant default. The incumbent is demoted
 * server-side — a tenant with templates always has exactly one default.
 */
export function useSetDefaultScopeOfWorkTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<ScopeOfWorkTemplate>(
        operationsClient,
        settingsEndpoints.scopeOfWorkTemplateDefault(id),
        {},
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.scopeOfWorkTemplates() });
    },
  });
}

export function useDeleteScopeOfWorkTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiDelete(operationsClient, settingsEndpoints.scopeOfWorkTemplateDetail(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.scopeOfWorkTemplates() });
    },
  });
}
